import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Loader2, ChevronDown } from 'lucide-react';
import { auth } from '../firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { verifyFirebaseToken, sendOTP, verifyOTP, resendOTP, completeSignup, API_BASE_URL } from '../api';
import { getStoredInfluencerCoupon } from '../utils/influencerAttribution';
import { trackEvent } from '../utils/analytics';
import UserPersonaPicker from './UserPersonaPicker';
import './landing/atelier/atelier.css';

const getOtpDeliveryMessage = (response, fallback = 'OTP sent successfully') => (
  response?.data?.message || fallback
);

const getOtpDeliveryChannel = (response) => (
  response?.data?.data?.channel || null
);

const getOtpStepHint = (channel) => {
  if (channel === 'whatsapp') {
    return 'Enter the 6-digit code sent to your WhatsApp at';
  }
  if (channel === 'sms') {
    return 'Enter the 6-digit code sent via SMS to';
  }
  return 'Enter the 6-digit code sent to';
};

// Common country codes with flags and names
const COUNTRY_CODES = [
  { code: '+1', name: 'US', flag: '🇺🇸', label: 'United States' },
  { code: '+44', name: 'GB', flag: '🇬🇧', label: 'United Kingdom' },
  { code: '+91', name: 'IN', flag: '🇮🇳', label: 'India' },
  { code: '+61', name: 'AU', flag: '🇦🇺', label: 'Australia' },
  { code: '+86', name: 'CN', flag: '🇨🇳', label: 'China' },
  { code: '+81', name: 'JP', flag: '🇯🇵', label: 'Japan' },
  { code: '+49', name: 'DE', flag: '🇩🇪', label: 'Germany' },
  { code: '+33', name: 'FR', flag: '🇫🇷', label: 'France' },
  { code: '+39', name: 'IT', flag: '🇮🇹', label: 'Italy' },
  { code: '+34', name: 'ES', flag: '🇪🇸', label: 'Spain' },
  { code: '+7', name: 'RU', flag: '🇷🇺', label: 'Russia' },
  { code: '+55', name: 'BR', flag: '🇧🇷', label: 'Brazil' },
  { code: '+52', name: 'MX', flag: '🇲🇽', label: 'Mexico' },
  { code: '+82', name: 'KR', flag: '🇰🇷', label: 'South Korea' },
  { code: '+65', name: 'SG', flag: '🇸🇬', label: 'Singapore' },
  { code: '+971', name: 'AE', flag: '🇦🇪', label: 'UAE' },
  { code: '+966', name: 'SA', flag: '🇸🇦', label: 'Saudi Arabia' },
  { code: '+27', name: 'ZA', flag: '🇿🇦', label: 'South Africa' },
  { code: '+31', name: 'NL', flag: '🇳🇱', label: 'Netherlands' },
  { code: '+46', name: 'SE', flag: '🇸🇪', label: 'Sweden' },
  { code: '+47', name: 'NO', flag: '🇳🇴', label: 'Norway' },
  { code: '+41', name: 'CH', flag: '🇨🇭', label: 'Switzerland' },
  { code: '+32', name: 'BE', flag: '🇧🇪', label: 'Belgium' },
  { code: '+351', name: 'PT', flag: '🇵🇹', label: 'Portugal' },
  { code: '+358', name: 'FI', flag: '🇫🇮', label: 'Finland' },
  { code: '+45', name: 'DK', flag: '🇩🇰', label: 'Denmark' },
  { code: '+353', name: 'IE', flag: '🇮🇪', label: 'Ireland' },
  { code: '+64', name: 'NZ', flag: '🇳🇿', label: 'New Zealand' },
  { code: '+60', name: 'MY', flag: '🇲🇾', label: 'Malaysia' },
  { code: '+66', name: 'TH', flag: '🇹🇭', label: 'Thailand' },
  { code: '+62', name: 'ID', flag: '🇮🇩', label: 'Indonesia' },
  { code: '+63', name: 'PH', flag: '🇵🇭', label: 'Philippines' },
  { code: '+84', name: 'VN', flag: '🇻🇳', label: 'Vietnam' },
  { code: '+92', name: 'PK', flag: '🇵🇰', label: 'Pakistan' },
  { code: '+880', name: 'BD', flag: '🇧🇩', label: 'Bangladesh' },
  { code: '+94', name: 'LK', flag: '🇱🇰', label: 'Sri Lanka' },
  { code: '+977', name: 'NP', flag: '🇳🇵', label: 'Nepal' },
  { code: '+20', name: 'EG', flag: '🇪🇬', label: 'Egypt' },
  { code: '+234', name: 'NG', flag: '🇳🇬', label: 'Nigeria' },
  { code: '+254', name: 'KE', flag: '🇰🇪', label: 'Kenya' },
  { code: '+212', name: 'MA', flag: '🇲🇦', label: 'Morocco' },
  { code: '+90', name: 'TR', flag: '🇹🇷', label: 'Turkey' },
  { code: '+972', name: 'IL', flag: '🇮🇱', label: 'Israel' },
  { code: '+961', name: 'LB', flag: '🇱🇧', label: 'Lebanon' },
  { code: '+974', name: 'QA', flag: '🇶🇦', label: 'Qatar' },
  { code: '+965', name: 'KW', flag: '🇰🇼', label: 'Kuwait' },
  { code: '+973', name: 'BH', flag: '🇧🇭', label: 'Bahrain' },
  { code: '+968', name: 'OM', flag: '🇴🇲', label: 'Oman' },
  { code: '+961', name: 'JO', flag: '🇯🇴', label: 'Jordan' },
];

// Map ISO country codes to phone country codes
const COUNTRY_CODE_MAP = {};
COUNTRY_CODES.forEach(country => {
  COUNTRY_CODE_MAP[country.name] = country.code;
});

// Normalize the local part of a phone number so we never double up the country
// code. Users often type their full number (e.g. "918860572779") into the local
// field while the selector already provides "+91", which would otherwise produce
// "+91918860572779" and register a brand new user. We also drop leading trunk
// zeros (e.g. "098..." -> "98...").
const getLocalPhoneDigits = (countryCode, rawPhone) => {
  const ccDigits = String(countryCode).replace(/\D/g, ''); // e.g. '91'
  let digits = String(rawPhone).replace(/\D/g, '');

  // Strip a leading duplicate country code only when the remaining number is
  // still a plausible local length (> 10 digits implies the code was included).
  if (ccDigits && digits.length > 10 && digits.startsWith(ccDigits)) {
    digits = digits.slice(ccDigits.length);
  }

  // Drop leading trunk/zero prefixes that aren't part of the E.164 number.
  digits = digits.replace(/^0+/, '');

  return digits;
};

// Build the full E.164 number (country code + normalized local digits).
const buildFullPhoneNumber = (countryCode, rawPhone) =>
  `${countryCode}${getLocalPhoneDigits(countryCode, rawPhone)}`;

// Function to detect country from IP address with timeout
const detectCountryFromIP = async () => {
  const timeout = 3000; // 3 seconds timeout
  
  const fetchWithTimeout = (url, options = {}) => {
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), timeout)
      )
    ]);
  };
  
  try {
    // Using ipapi.co free service (no API key required for basic usage)
    const response = await fetchWithTimeout('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch location');
    }
    
    const data = await response.json();
    const countryCodeISO = data.country_code; // e.g., 'US', 'IN', 'GB'
    
    // Map ISO country code to phone country code
    if (countryCodeISO && COUNTRY_CODE_MAP[countryCodeISO]) {
      return COUNTRY_CODE_MAP[countryCodeISO];
    }
    
    return null;
  } catch (error) {
    console.log('Country detection failed:', error);
    // Fallback: try alternative service
    try {
      const response = await fetchWithTimeout('https://ip-api.com/json/?fields=countryCode', {
        method: 'GET',
      });
      
      if (response.ok) {
        const data = await response.json();
        const countryCodeISO = data.countryCode;
        
        if (countryCodeISO && COUNTRY_CODE_MAP[countryCodeISO]) {
          return COUNTRY_CODE_MAP[countryCodeISO];
        }
      }
    } catch (fallbackError) {
      console.log('Fallback country detection also failed:', fallbackError);
    }
    
    return null;
  }
};

/** Atelier-themed shell for every auth step: dark page, brand panel (desktop), light form card. */
const AUTH_SWATCHES = [
  { background: '#3b4f7a', backgroundImage: 'linear-gradient(90deg,#ffffff1a 50%,transparent 50%),linear-gradient(#ffffff1a 50%,transparent 50%)', backgroundSize: '22px 22px' },
  { background: '#7a2f2a', backgroundImage: 'repeating-linear-gradient(45deg,#ffffff12 0 3px,transparent 3px 8px)' },
  { background: '#d9cdb4', backgroundImage: 'repeating-linear-gradient(0deg,#00000010 0 1px,transparent 1px 4px),repeating-linear-gradient(90deg,#00000010 0 1px,transparent 1px 4px)' },
  { background: '#2f5a48', backgroundImage: 'repeating-linear-gradient(-45deg,#00000026 0 6px,transparent 6px 12px)' },
  { background: '#e8b04a', backgroundImage: 'repeating-linear-gradient(90deg,#00000014 0 2px,transparent 2px 6px)' },
];

const AuthLayout = ({ backLabel, onBack, wide = false, children }) => (
  <div className="atl-root atl-auth">
    <header className="atl-auth-top">
      {onBack ? (
        <button type="button" onClick={onBack} className="atl-link atl-auth-back">
          <ArrowLeft size={18} aria-hidden="true" />
          <span>{backLabel}</span>
        </button>
      ) : <span />}
      <span className="atl-logo atl-auth-logo">
        <img className="atl-logo-mark" src="/logo.png?v=20260711" alt="" />fashdeck
      </span>
    </header>

    <main className={`atl-auth-main${wide ? ' atl-auth-main--wide' : ''}`}>
      <section className="atl-auth-brand" aria-hidden="true">
        <div className="atl-pill"><span className="atl-dot" />Fabric to fashion, powered by AI</div>
        <h1>Design with fabric that actually exists.</h1>
        <p>Discover sourceable fabrics, generate AI concepts on them, and send buyers a catalogue — 10x faster.</p>
        <div className="atl-auth-swatches">
          {AUTH_SWATCHES.map((style, i) => <span key={i} style={style} />)}
        </div>
      </section>

      <section className="atl-auth-card">{children}</section>
    </main>
  </div>
);

const OTPLogin = ({ onLoginSuccess, showNotification, onBack }) => {
  const isNativePlatform = (() => {
    try {
      return Capacitor.isNativePlatform();
    } catch (_) {
      return false;
    }
  })();
  const isCapacitorLocalhost = typeof window !== 'undefined' && window.location?.origin === 'https://localhost';
  // Default all platforms (web + app) to backend OTP flow (no Firebase reCAPTCHA).
  // Set REACT_APP_USE_FIREBASE_OTP=true only if you explicitly want Firebase OTP flow.
  const forceBackendOtp = process.env.REACT_APP_USE_FIREBASE_OTP !== 'true';
  const useBackendOtpWithoutRecaptcha =
    forceBackendOtp || process.env.REACT_APP_DISABLE_RECAPTCHA === 'true' || isNativePlatform || isCapacitorLocalhost;

  const [step, setStep] = useState('phone'); // 'phone', 'otp', 'profile'
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [googleRedirecting, setGoogleRedirecting] = useState(false);
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91'); // Default to India
  const [isDetectingCountry, setIsDetectingCountry] = useState(true);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [otp, setOTP] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [userPersonas, setUserPersonas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [confirmationResult, setConfirmationResult] = useState(null);
  /** E.164 phone locked when OTP is sent — verify/resend must use this, not live countryCode. */
  const [otpSessionPhone, setOtpSessionPhone] = useState('');
  const [otpDeliveryChannel, setOtpDeliveryChannel] = useState(null);
  const countryDropdownRef = useRef(null);
  
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const recaptchaContainerRef = useRef(null);
  const hiddenOtpInputRef = useRef(null);
  const otpDigitsRef = useRef(['', '', '', '', '', '']);
  const isVerifyingRef = useRef(false);
  const autoVerifyTimerRef = useRef(null);
  /** Sync phone for verify; set immediately when OTP is sent (before React re-render). */
  const otpSessionPhoneRef = useRef('');

  const getOtpSessionPhone = () =>
    otpSessionPhoneRef.current || otpSessionPhone || buildFullPhoneNumber(countryCode, phone);

  const resetOtpDigits = () => {
    otpDigitsRef.current = ['', '', '', '', '', ''];
    setOTP(['', '', '', '', '', '']);
    if (hiddenOtpInputRef.current) {
      hiddenOtpInputRef.current.value = '';
    }
  };

  const clearOtpSession = () => {
    otpSessionPhoneRef.current = '';
    setOtpSessionPhone('');
    setOtpDeliveryChannel(null);
    setConfirmationResult(null);
  };

  const startOtpSession = (phoneNumber, channel = null) => {
    otpSessionPhoneRef.current = phoneNumber;
    setOtpSessionPhone(phoneNumber);
    setOtpDeliveryChannel(channel);
    setConfirmationResult({ mode: 'backend-otp' });
  };

  const applyOtpDigits = (digits) => {
    const normalized = Array.from({ length: 6 }, (_, index) => digits[index] || '');
    otpDigitsRef.current = normalized;
    setOTP([...normalized]);
    return normalized;
  };

  const getCompleteOtp = () => otpDigitsRef.current.join('');

  const scheduleAutoVerify = (otpValue) => {
    if (!otpSessionPhoneRef.current && !confirmationResult) {
      return;
    }
    if (autoVerifyTimerRef.current) {
      clearTimeout(autoVerifyTimerRef.current);
    }
    // Brief delay lets SMS/browser autofill finish before we verify.
    autoVerifyTimerRef.current = setTimeout(() => {
      autoVerifyTimerRef.current = null;
      handleVerifyOTP(otpValue);
    }, 150);
  };

  useEffect(() => () => {
    if (autoVerifyTimerRef.current) {
      clearTimeout(autoVerifyTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (step === 'otp' && otpRefs[0].current) {
      otpRefs[0].current.focus();
    }
  }, [step]);

  // Detect country from IP on mount — only before the user starts an OTP session.
  useEffect(() => {
    const detectCountry = async () => {
      setIsDetectingCountry(true);
      const detectedCode = await detectCountryFromIP();

      if (detectedCode && !otpSessionPhoneRef.current) {
        setCountryCode(detectedCode);
      }

      setIsDetectingCountry(false);
    };

    detectCountry();
  }, []);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
        setCountrySearchTerm('');
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCountryDropdown]);

  // Initialize reCAPTCHA once and reuse
  useEffect(() => {
    console.log('OTP mode:', useBackendOtpWithoutRecaptcha ? 'backend-no-recaptcha' : 'firebase-recaptcha');
  }, [useBackendOtpWithoutRecaptcha]);

  useEffect(() => {
    if (useBackendOtpWithoutRecaptcha) {
      return;
    }

    let isMounted = true;
    const init = async () => {
      try {
        if (!window.recaptchaVerifier && recaptchaContainerRef.current) {
          const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
            callback: () => {},
            'expired-callback': () => {
              showNotification('reCAPTCHA expired. Please try again.', 'error');
            }
          });
          window.recaptchaVerifier = verifier;
          try { await verifier.render(); } catch (_) {}
        }
      } catch (_) {}
    };
    init();
    return () => {
      if (!isMounted) return;
      try {
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
        }
      } catch (_) {}
      window.recaptchaVerifier = null;
      isMounted = false;
    };
  }, [useBackendOtpWithoutRecaptcha]);

  const setupRecaptcha = async () => {
    if (window.recaptchaVerifier) {
      try { await window.recaptchaVerifier.render(); } catch (_) {}
      return window.recaptchaVerifier;
    }
    const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {},
      'expired-callback': () => {
        showNotification('reCAPTCHA expired. Please try again.', 'error');
      }
    });
    window.recaptchaVerifier = verifier;
    try { await verifier.render(); } catch (_) {}
    return verifier;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    const cleanPhone = getLocalPhoneDigits(countryCode, phone);
    // Minimum 7 digits, maximum 15 digits (E.164 standard)
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      showNotification('Please enter a valid phone number (7-15 digits)', 'error');
      return;
    }

    // Japan mobiles are 10 national digits starting 60/70/80/90 (after dropping trunk 0).
    // Catch the common mistake of pairing Indian digits with +81 before the API call.
    if (countryCode === '+81' && !/^(60|70|80|90)\d{8}$/.test(cleanPhone)) {
      showNotification(
        'That doesn\'t look like a valid Japan mobile number. Check the country code and try again.',
        'error'
      );
      return;
    }

    setIsLoading(true);
    try {
      const phoneNumber = buildFullPhoneNumber(countryCode, phone);

      if (useBackendOtpWithoutRecaptcha) {
        const response = await sendOTP(phoneNumber);
        const channel = getOtpDeliveryChannel(response);
        startOtpSession(phoneNumber, channel);
        showNotification(getOtpDeliveryMessage(response, 'OTP sent successfully'), 'success');
      } else {
        const appVerifier = await setupRecaptcha();
        const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
        otpSessionPhoneRef.current = phoneNumber;
        setOtpSessionPhone(phoneNumber);
        setOtpDeliveryChannel('sms');
        setConfirmationResult(confirmation);
        showNotification('OTP sent to your mobile number', 'success');
      }

      setStep('otp');
      setCountdown(30);
      resetOtpDigits();
    } catch (error) {
      console.error('OTP Send Error:', error);
      
      let errorMessage = 'Failed to send OTP';
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please contact support.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message && error.message !== 'Network Error') {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, 'error');
      
      // Attempt to reset the existing reCAPTCHA instead of recreating
      try {
        if (window.recaptchaVerifier) {
          const widgetId = await window.recaptchaVerifier.render();
          if (window.grecaptcha && widgetId !== undefined) {
            window.grecaptcha.reset(widgetId);
          }
        }
      } catch (_) {}
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const digits = [...otpDigitsRef.current];

    if (value.length > 1) {
      // SMS/WhatsApp autofill can inject multiple digits into one input.
      value.replace(/\D/g, '').slice(0, 6 - index).split('').forEach((char, offset) => {
        if (index + offset < 6) {
          digits[index + offset] = char;
        }
      });
    } else if (value) {
      digits[index] = value.slice(-1);
    } else {
      digits[index] = '';
    }

    const normalized = applyOtpDigits(digits);

    if (value && index < 5 && value.length === 1) {
      otpRefs[index + 1].current?.focus();
    }

    const completeOTP = normalized.join('');
    if (completeOTP.length === 6 && normalized.every(Boolean)) {
      scheduleAutoVerify(completeOTP);
    }
  };

  const handleAutofillOtp = (rawValue) => {
    const digits = String(rawValue).replace(/\D/g, '').slice(0, 6).split('');
    if (digits.length === 0) return;

    const normalized = applyOtpDigits(digits);
    const completeOTP = normalized.join('');
    if (completeOTP.length === 6 && normalized.every(Boolean)) {
      scheduleAutoVerify(completeOTP);
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigitsRef.current[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    handleAutofillOtp(e.clipboardData.getData('text/plain'));
    otpRefs[5].current?.focus();
  };

  const handleVerifyOTP = async (otpString = null) => {
    const otpToVerify = String(otpString ?? getCompleteOtp()).replace(/\D/g, '');

    if (otpToVerify.length !== 6) {
      showNotification('Please enter the complete 6-digit OTP', 'error');
      return;
    }

    if (!confirmationResult && !otpSessionPhoneRef.current) {
      showNotification('Please request OTP first', 'error');
      return;
    }

    if (isVerifyingRef.current) {
      return;
    }

    isVerifyingRef.current = true;
    setIsLoading(true);
    try {
      if (useBackendOtpWithoutRecaptcha) {
        const phoneNumber = getOtpSessionPhone();
        const response = await verifyOTP(phoneNumber, otpToVerify);
        const { token, user, needsProfile } = response.data.data || {};

        if (needsProfile) {
          setStep('profile');
          showNotification('Welcome! Complete your profile to finish signup.', 'success');
          return;
        }

        localStorage.setItem('token', token);
        if (user.role) {
          localStorage.setItem('role', user.role);
        }
        localStorage.setItem('isInfluencerAgent', user.isInfluencerAgent ? 'true' : 'false');
        showNotification(response.data.message || 'Login successful!', 'success');
        onLoginSuccess();
      } else {
        // Verify OTP with Firebase
        const firebaseUserCredential = await confirmationResult.confirm(otpToVerify);
        const firebaseUser = firebaseUserCredential.user;
        
        // Get Firebase ID token
        const firebaseToken = await firebaseUser.getIdToken();
        
        // Verify with backend and create/login user
        const influencerCode = getStoredInfluencerCoupon();
        const response = await verifyFirebaseToken(
          firebaseToken,
          firebaseUser.phoneNumber,
          name,
          influencerCode || undefined
        );
        
        const { token, user, needsProfile } = response.data.data;
        
        if (needsProfile) {
          setStep('profile');
          showNotification('Welcome! Complete your profile to finish signup.', 'success');
        } else {
          localStorage.setItem('token', token);
          if (user.role) {
            localStorage.setItem('role', user.role);
          }
          localStorage.setItem('isInfluencerAgent', user.isInfluencerAgent ? 'true' : 'false');
          showNotification(response.data.message || 'Login successful!', 'success');
          onLoginSuccess();
        }
      }
    } catch (error) {
      console.error('OTP Verification Error:', error);
      
      let errorMessage = 'Invalid OTP';
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid OTP. Please check and try again.';
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'OTP expired. Please request a new one.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showNotification(errorMessage, 'error');
      resetOtpDigits();
      otpRefs[0].current?.focus();
    } finally {
      isVerifyingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      showNotification('Please enter your name', 'error');
      return;
    }

    if (userPersonas.length === 0) {
      showNotification('Please select at least one business persona', 'error');
      return;
    }

    if (!confirmationResult && !otpSessionPhoneRef.current) {
      showNotification('Session expired. Please request OTP again', 'error');
      setStep('phone');
      return;
    }

    setIsLoading(true);
    try {
      const phoneNumber = getOtpSessionPhone();

      if (useBackendOtpWithoutRecaptcha) {
        const response = await completeSignup(phoneNumber, name.trim(), userPersonas);
        const { token, user } = response.data.data;
        localStorage.setItem('token', token);
        if (user.role) {
          localStorage.setItem('role', user.role);
        }
        localStorage.setItem('isInfluencerAgent', user.isInfluencerAgent ? 'true' : 'false');
        showNotification(`Welcome ${name}!`, 'success');
        onLoginSuccess();
        return;
      }

      const firebaseUser = auth.currentUser;
      
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const firebaseToken = await firebaseUser.getIdToken();
      
      const influencerCode = getStoredInfluencerCoupon();
      const response = await verifyFirebaseToken(
        firebaseToken,
        firebaseUser.phoneNumber,
        name.trim(),
        influencerCode || undefined,
        userPersonas
      );
      
      const { token, user } = response.data.data;
      localStorage.setItem('token', token);
      if (user.role) {
        localStorage.setItem('role', user.role);
      }
      localStorage.setItem('isInfluencerAgent', user.isInfluencerAgent ? 'true' : 'false');
      showNotification(`Welcome ${name}!`, 'success');
      onLoginSuccess();
    } catch (error) {
      console.error('Profile submit error:', error);
      showNotification(
        error.response?.data?.message || 'Failed to create account',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;

    setIsLoading(true);
    try {
      const phoneNumber = getOtpSessionPhone();

      if (useBackendOtpWithoutRecaptcha) {
        const response = await resendOTP(phoneNumber);
        const channel = getOtpDeliveryChannel(response);
        startOtpSession(phoneNumber, channel);
        showNotification(getOtpDeliveryMessage(response, 'OTP resent successfully'), 'success');
      } else {
        // Reset and reuse reCAPTCHA
        const appVerifier = await setupRecaptcha();
        try {
          const widgetId = await appVerifier.render();
          if (window.grecaptcha && widgetId !== undefined) {
            window.grecaptcha.reset(widgetId);
          }
        } catch (_) {}
        
        const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
        setConfirmationResult(confirmation);
      }

      if (!useBackendOtpWithoutRecaptcha) {
        showNotification('OTP resent successfully', 'success');
      }
      setCountdown(30);
      resetOtpDigits();
      otpRefs[0].current?.focus();
    } catch (error) {
      console.error('OTP Resend Error:', error);
      showNotification(
        error.response?.data?.message || error.message || 'Failed to resend OTP',
        'error'
      );
      
      // Reset reCAPTCHA on error
      if (!useBackendOtpWithoutRecaptcha && window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleRedirecting(true);
    trackEvent('login_google_click', { source: 'otp_login' });
    try {
      const base = String(API_BASE_URL || '/api').replace(/\/+$/, '');
      const apiRoot = base.endsWith('/api') ? base : `${base}/api`;
      window.location.href = `${apiRoot}/auth/google`;
    } catch (err) {
      showNotification('Google authentication failed', 'error');
      setGoogleRedirecting(false);
    }
  };

  const countryMatches = COUNTRY_CODES.filter((country) => {
    const search = countrySearchTerm.toLowerCase();
    return (
      country.label.toLowerCase().includes(search) ||
      country.code.includes(search) ||
      country.name.toLowerCase().includes(search)
    );
  });

  // Phone Input Step
  if (step === 'phone') {
    return (
      <AuthLayout backLabel="Back" onBack={onBack}>
        <h2 className="atl-auth-title">Welcome back</h2>
        <p className="atl-auth-sub">Sign in with Google, or use your mobile number.</p>

        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isLoading || googleRedirecting}
          className="atl-auth-btn atl-auth-btn--google"
        >
          {googleRedirecting ? (
            <Loader2 size={20} className="atl-spin" />
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <div className="atl-auth-divider"><span>or</span></div>

        {!showPhoneLogin ? (
          <button
            type="button"
            onClick={() => setShowPhoneLogin(true)}
            className="atl-auth-btn atl-auth-btn--secondary"
          >
            Continue with mobile number
          </button>
        ) : (
          <form onSubmit={handleSendOTP}>
            <label className="atl-auth-label" htmlFor="atl-auth-phone">Mobile number</label>
            <div className="atl-auth-phone-row">
              {/* Country Code Selector */}
              <div className="atl-auth-country" ref={countryDropdownRef}>
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={showCountryDropdown}
                  onClick={() => {
                    setShowCountryDropdown(!showCountryDropdown);
                    if (showCountryDropdown) {
                      setCountrySearchTerm('');
                    }
                  }}
                  className="atl-auth-input atl-auth-country-btn"
                >
                  <span className="atl-auth-flag">{COUNTRY_CODES.find(c => c.code === countryCode)?.flag || '🇮🇳'}</span>
                  <span>{countryCode}</span>
                  <ChevronDown size={16} aria-hidden="true" />
                </button>

                {showCountryDropdown && (
                  <div className="atl-auth-country-menu">
                    <input
                      type="text"
                      placeholder="Search country…"
                      value={countrySearchTerm}
                      className="atl-auth-input atl-auth-country-search"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setCountrySearchTerm(e.target.value)}
                      autoFocus
                    />
                    <div className="atl-auth-country-list" role="listbox">
                      {countryMatches.map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          role="option"
                          aria-selected={countryCode === country.code}
                          onClick={() => {
                            setCountryCode(country.code);
                            setShowCountryDropdown(false);
                            setCountrySearchTerm('');
                          }}
                          className={`atl-auth-country-opt${countryCode === country.code ? ' is-on' : ''}`}
                        >
                          <span className="atl-auth-flag">{country.flag}</span>
                          <span className="atl-auth-country-name">{country.label}</span>
                          <span className="atl-auth-country-code">{country.code}</span>
                        </button>
                      ))}
                      {countryMatches.length === 0 && (
                        <div className="atl-auth-country-empty">No countries found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Phone Number Input */}
              <input
                id="atl-auth-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="1234567890"
                className="atl-auth-input atl-auth-phone"
                required
                autoComplete="tel"
                inputMode="numeric"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || googleRedirecting}
              className="atl-auth-btn atl-auth-btn--primary"
            >
              {isLoading ? <Loader2 size={20} className="atl-spin" /> : 'Send OTP'}
            </button>
          </form>
        )}

        <p className="atl-auth-legal">By continuing, you agree to our Terms of Service and Privacy Policy.</p>

        {/* Firebase reCAPTCHA container (hidden for backend OTP mode) */}
        {!useBackendOtpWithoutRecaptcha && <div id="recaptcha-container"></div>}
      </AuthLayout>
    );
  }

  // OTP Verification Step
  if (step === 'otp') {
    return (
      <AuthLayout
        backLabel="Change number"
        onBack={() => {
          setStep('phone');
          resetOtpDigits();
          clearOtpSession();
        }}
      >
        <h2 className="atl-auth-title">Verify OTP</h2>
        {otpDeliveryChannel === 'whatsapp' && (
          <div className="atl-auth-note atl-auth-note--wa">
            OTP sent on WhatsApp. Open WhatsApp and check your chat for the 6-digit code.
          </div>
        )}
        {otpDeliveryChannel === 'sms' && (
          <div className="atl-auth-note atl-auth-note--sms">
            OTP sent via SMS. Check your messages for the 6-digit code.
          </div>
        )}
        <p className="atl-auth-sub">
          {getOtpStepHint(otpDeliveryChannel)}<br />
          <strong className="atl-auth-phone-echo">
            {otpSessionPhone
              ? otpSessionPhone.replace(/(\+\d{1,3})(\d+)/, '$1 $2')
              : `${countryCode} ${getLocalPhoneDigits(countryCode, phone)}`}
          </strong>
        </p>

        <div className="atl-auth-otp" onPaste={handleOTPPaste}>
          <input
            ref={hiddenOtpInputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-hidden="true"
            tabIndex={-1}
            className="atl-auth-otp-hidden"
            onChange={(e) => handleAutofillOtp(e.target.value)}
          />
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={otpRefs[index]}
              type="tel"
              inputMode="numeric"
              autoComplete="off"
              aria-label={`Digit ${index + 1}`}
              maxLength={index === 0 ? 6 : 1}
              value={digit}
              onChange={(e) => handleOTPChange(index, e.target.value)}
              onKeyDown={(e) => handleOTPKeyDown(index, e)}
              className="atl-auth-input atl-auth-otp-digit"
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => handleVerifyOTP(getCompleteOtp())}
          disabled={isLoading || otp.some(digit => !digit)}
          className="atl-auth-btn atl-auth-btn--primary"
        >
          {isLoading ? <Loader2 size={20} className="atl-spin" /> : 'Verify OTP'}
        </button>

        <div className="atl-auth-resend">
          {countdown > 0 ? (
            <p>Resend OTP in <strong>{countdown}s</strong></p>
          ) : (
            <button type="button" onClick={handleResendOTP} disabled={isLoading} className="atl-auth-textbtn">
              Resend OTP
            </button>
          )}
        </div>
      </AuthLayout>
    );
  }

  // Profile completion step (new users)
  if (step === 'profile') {
    return (
      <AuthLayout wide>
        <h2 className="atl-auth-title">Welcome aboard!</h2>
        <p className="atl-auth-sub">Tell us a bit about yourself.</p>

        <form onSubmit={handleProfileSubmit}>
          <label className="atl-auth-label" htmlFor="atl-auth-name">Your name</label>
          <input
            id="atl-auth-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your full name"
            className="atl-auth-input atl-auth-field"
            required
            autoComplete="name"
            autoFocus
          />

          <label className="atl-auth-label">
            Business persona <span className="atl-auth-req">*</span>
          </label>
          <p className="atl-auth-hint">Select all that apply to your business</p>
          <div className="atl-auth-persona">
            <UserPersonaPicker value={userPersonas} onChange={setUserPersonas} />
          </div>

          <button type="submit" disabled={isLoading} className="atl-auth-btn atl-auth-btn--primary">
            {isLoading ? <Loader2 size={20} className="atl-spin" /> : 'Complete registration'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return null;
};

export default OTPLogin;

